import {
  Avatar,
  Box,
  ClickAwayListener,
  Stack,
  Typography,
} from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary } from 'Hooks';
import { PurpleSearchIcon } from 'Images';
import { RefObject } from 'react';
import { sendEvent } from 'Utils/sendEvent';
import { SearchInput } from '../SearchInput';
import { SearchContent } from '../SearchContent';
import { ContentPaper } from '../ContentPaper';
import { IRelatedAction, IResult } from '../hooks/useSearchLogic';

interface SearchDropdownProps {
  isActive: boolean;
  value: string;
  results: IResult[];
  relatedActions: IRelatedAction[];
  isLoading: boolean;
  showNoResults: boolean;
  showResults: boolean;
  hasContent: boolean;
  minSearchLength: number;
  placeholder?: string;
  containerRef: RefObject<HTMLDivElement>;
  searchBarRef: RefObject<HTMLButtonElement>;
  inputRef: RefObject<HTMLInputElement>;
  onActivate: () => void;
  onClose: () => void;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onClear: () => void;
  onActivityClick: (action: IRelatedAction) => void;
}

export const SearchDropdown = ({
  isActive,
  value,
  results,
  relatedActions,
  isLoading,
  showNoResults,
  showResults,
  hasContent,
  minSearchLength,
  placeholder,
  containerRef,
  searchBarRef,
  inputRef,
  onActivate,
  onClose,
  onInputChange,
  onKeyDown,
  onClear,
  onActivityClick,
}: SearchDropdownProps) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.ActivitiesLobby);

  return (
    <Box
      ref={containerRef}
      sx={{ position: 'relative', width: '564px', mt: '24px' }}
    >
      {!isActive ? (
        <Stack
          ref={searchBarRef}
          component={'button'}
          sx={{
            width: '564px',
            height: '56px',
            flexDirection: 'row-reverse',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'white',
            border: '1px solid',
            borderColor: 'primary.shade_200',
            borderRadius: '30px',
            cursor: 'text',
            gap: '10px',
            px: '4px',
            '&:focus': { outlineColor: 'rgb(21, 82, 232)' },
          }}
          onClick={() => {
            sendEvent('Search Page', 'HomePageSearch');
            onActivate();
          }}
        >
          <Avatar
            sx={{ width: '44px', height: '44px' }}
            src={PurpleSearchIcon}
            alt=''
          />
          <Typography
            variant='body3'
            sx={{
              fontFamily: 'Assistant',
              pl: 2,
              fontSize: '20px',
              lineHeight: '24px',
              fontWeight: 400,
              color: 'text.primary',
            }}
          >
            {placeholder || dictionary.searchButton}
          </Typography>
        </Stack>
      ) : (
        <ClickAwayListener onClickAway={onClose}>
          <Box>
            <SearchInput
              value={value}
              inputRef={inputRef}
              useCurtain={false}
              onInputChange={onInputChange}
              onKeyDown={onKeyDown}
              onClear={onClear}
            />
            {(hasContent || value.length > 0) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '72px',
                  left: 0,
                  zIndex: 99999,
                }}
              >
                <ContentPaper>
                  <SearchContent
                    value={value}
                    results={results}
                    relatedActions={relatedActions}
                    isLoading={isLoading}
                    showNoResults={showNoResults}
                    showResults={showResults}
                    minSearchLength={minSearchLength}
                    onActivityClick={onActivityClick}
                  />
                </ContentPaper>
              </Box>
            )}
          </Box>
        </ClickAwayListener>
      )}
    </Box>
  );
};
